/**
 * Store state shape + initial state — VERBATIM from source (component.js:2-64).
 * Plus the transient class fields (_tid) folded into state for reducer purity.
 */
import type {
  Adapter,
  AuditEntry,
  Conn,
  ConnectStep,
  CurrentUser,
  Direction,
  OutboundHook,
  Role,
  RightTab,
  Surface,
  Version,
} from '../domain/types';
import type { AuthCfg } from '../domain/auth';
import type { MetaOverride } from '../domain/catalog';
import type { Resolutions } from '../domain/mapping';

export type Toast = { id: number; msg: string; tone: string };

/** Replace-route confirm payload. */
export type ReplaceState = { product: string; dataType: string; fromId: string; toId: string } | null;

/** Meta-manage modal working copy. */
export type MetaManageState =
  | { id: string; name: string; category: string; change: Adapter['change']; purpose: string }
  | null;

/** MCP-manage modal working copy. */
// `tools` holds the REAL tool names discovered from the live MCP server (via the
// backend), so the drawer shows actual vendor tools instead of category mocks.
// `loading` is true while that fetch is in flight on open/resync.
export type McpManageTool = { name: string; kind: string; isNew?: boolean };
export type McpManageState =
  | {
      id: string;
      url: string;
      transport: 'sse' | 'stdio';
      category: string;
      resyncing: boolean;
      extraTool: boolean;
      tools?: McpManageTool[];
      loading?: boolean;
      // True for built-in/managed connectors (HubSpot/Zoho): Server URL +
      // Transport render read-only (the connector definition owns the URL, not
      // the tenant). Set from the BE `managed` flag on open; undefined → editable
      // (user-ingested MCP connectors, where the user owns the URL).
      managed?: boolean;
    }
  | null;

/** Ingest wizard working state (the step machine). */
export type IngestState = {
  step: 'kind' | 'mcp' | 'mcpDiscover' | 'mcpDone' | 'source' | 'duplicate' | 'ingesting' | 'done';
  mode?: 'new' | 'version';
  sourceType: 'openapi' | 'prose';
  url: string;
  progress: number;
  result: import('../domain/ingest').IngestResult | null;
  category?: string | null;
  categoryTouched?: boolean;
  mcpUrl?: string;
  mcpTransport?: 'sse' | 'stdio';
  mcpName?: string;
  detectedName?: string;
  dupAdapterId?: string;
  versionVendor?: string;
  targetAdapterId?: string;
} | null;

export type WebhookCfg = { secret?: string; verified?: boolean; events?: { type: string; ago: string }[] };

export type State = {
  surface: Surface;
  query: string;
  filter: 'all' | 'connected' | 'available';
  groupBy: 'none' | 'product' | 'company' | 'adapter' | 'status';
  detailId: string | null;
  connectId: string | null;
  connectStep: ConnectStep;
  connectKey: string;
  connectMethod: 'oauth' | 'apikey' | 'bearer';
  // MCP-OAuth: connector slug currently mid-OAuth-popup (set when the consent
  // window opens, cleared when the BE callback posts back). null when idle.
  oauthPendingId: string | null;
  grantId: string | null;
  // Mapping Plane "Map with AI": connector slug whose AI-mapping modal is open
  // (null when closed). The modal fetches/proposes/probes via the BE itself.
  mapAiId: string | null;
  replace: ReplaceState;
  mcpManage: McpManageState;
  metaManage: MetaManageState;
  metaOverrides: Record<string, MetaOverride>;
  mcpOverrides: Record<string, { url?: string; transport?: 'sse' | 'stdio'; extraTool?: boolean }>;
  logsId: string | null;
  logsTab: 'exec' | 'audit';
  audit: Record<string, AuditEntry[]>;
  routes: Record<string, string>;
  conn: Record<string, Conn>;
  direction: Direction;
  rightTab: RightTab;
  selectedNode: string | null;
  expandedField: string | null;
  fieldSearch: string;
  overrides: Record<string, string>;
  resolutions: Resolutions;
  remember: { subject: boolean; name: boolean };
  // Real, live per-field candidate mapping for the active adapter+entity+
  // direction (AI Integration Builder canvas), fetched from the BE's
  // entity-mapping/auto endpoint. null = not loaded yet / no real connection
  // for this adapter — useBuilder falls back to the seeds.ts mock shape only
  // in that case, never silently mixing real and mock data.
  entityMapping: import('../api/builder').EntityMappingDraft | null;
  entityMappingLoading: boolean;
  entityMappingError: string | null;
  changeDelivery: 'webhook' | 'poll';
  pollInterval: '1' | '5' | '15' | '60';
  authCfg: Record<string, AuthCfg>;
  webhookCfg: Record<string, WebhookCfg>;
  outboundHooks: Record<string, OutboundHook[]>;
  outDraftUrl: string;
  outDraftEvents: string[];
  versionPair: string;
  versions: Record<string, Version[]>;
  versionSel: Record<string, string>;
  addVersionOpen: boolean;
  newVersionStr: string;
  approveOpen: boolean;
  approver: string;
  approved: boolean;
  currentUser: CurrentUser;
  role: Role;
  adapters: Adapter[];
  activeAdapterId: string;
  ingest: IngestState;
  toasts: Toast[];
  _tid: number;
  // Set when the boot directory load fails against the real backend (USE_BACKEND
  // on). Non-null blocks the app on an error banner instead of silently running
  // on mock seed data that looks identical to live state (see AppShell).
  directoryLoadError: string | null;
  // FE↔BE integration bookkeeping (populated by the boot directory load). These
  // bridge the FE's slug-keyed world to the BE's UUID-keyed world so connect/
  // revoke/grant calls can resolve the right ids. Empty when USE_BACKEND is off.
  connBySlug: Record<string, string>; // connector slug → BE connection_id
  slugById: Record<string, string>; // BE integration_id → connector slug
  grantIdByRoute: Record<string, string>; // FE "product:dataType" → BE grant_id (for revoke)
  // Round 4: observability data loaded when the Logs modal opens, keyed by slug.
  // null = not yet loaded / no backend; the modal falls back to synthesized rows.
  obs: Record<string, import('../api/observability').ObservabilityData>;
};

export const initialState: State = {
  surface: 'directory',
  query: '',
  filter: 'all',
  groupBy: 'product',
  detailId: null,
  connectId: null,
  connectStep: 'idle',
  connectKey: '',
  connectMethod: 'oauth',
  oauthPendingId: null,
  grantId: null,
  mapAiId: null,
  replace: null,
  mcpManage: null,
  metaManage: null,
  metaOverrides: {},
  mcpOverrides: {},
  logsId: null,
  logsTab: 'exec',
  audit: {},
  routes: { 'Calendar:scheduling': 'google-calendar', 'CRM:contacts': 'ezyvet' },
  conn: {
    'google-calendar': { connected: true, status: 'active', vendorVersion: 'v3' },
    ezyvet: { connected: true, status: 'active', vendorVersion: '2.7' },
    zendesk: { connected: true, status: 'expired', vendorVersion: 'v2' },
  },
  direction: 'outbound',
  rightTab: 'auth',
  selectedNode: null,
  expandedField: null,
  fieldSearch: '',
  overrides: {},
  resolutions: { subject: null, name: null },
  remember: { subject: false, name: false },
  entityMapping: null,
  entityMappingLoading: false,
  entityMappingError: null,
  changeDelivery: 'webhook',
  pollInterval: '5',
  authCfg: {},
  webhookCfg: {},
  outboundHooks: {},
  outDraftUrl: '',
  outDraftEvents: ['created', 'updated'],
  versionPair: 'v1 × 2.7',
  versions: {
    ezyvet: [
      { id: 'ezyvet|2.7', catalog: 'v1', vendor: '2.7', status: 'draft', tag: null, rev: 0, execs: 0 },
      { id: 'ezyvet|2.6', catalog: 'v1', vendor: '2.6', status: 'published', tag: 'N-1', rev: 4, execs: 18420 },
      { id: 'ezyvet|2.5', catalog: 'v1', vendor: '2.5', status: 'deprecated', tag: null, rev: 2, execs: 5130 },
    ],
  },
  versionSel: { ezyvet: 'ezyvet|2.7' },
  addVersionOpen: false,
  newVersionStr: '',
  approveOpen: false,
  approver: '',
  approved: false,
  currentUser: { name: 'Maya Chen', role: 'Integration reviewer', email: 'maya.chen@softknack.com', initials: 'MC' },
  role: 'softknack',
  adapters: [
    { id: 'ezyvet', name: 'ezyVet', initials: 'EZ', color: '#0a8a87', cls: 'custom', source: 'ingested from OpenAPI', version: '2.7' },
  ],
  activeAdapterId: 'ezyvet',
  ingest: null,
  toasts: [],
  _tid: 0,
  directoryLoadError: null,
  // Filled by the boot directory load when USE_BACKEND is on.
  connBySlug: {},
  slugById: {},
  grantIdByRoute: {},
  obs: {},
};
